import { isCodedError } from './CodedError';

const getNonEmptyString = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value : undefined;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isMarkup = (value: string) => value.trimStart().startsWith('<');

const getPayloadMessage = (payload: unknown): string | undefined => {
  const directMessage = getNonEmptyString(payload);
  if (directMessage) return isMarkup(directMessage) ? undefined : directMessage;
  if (!isRecord(payload)) return undefined;

  const firstError = Array.isArray(payload.errors)
    ? payload.errors[0]
    : undefined;
  const jsonApiMessage = isRecord(firstError)
    ? (getNonEmptyString(firstError.detail) ??
      getNonEmptyString(firstError.title))
    : undefined;

  return (
    jsonApiMessage ??
    getPayloadMessage(payload.data) ??
    getNonEmptyString(payload.detail) ??
    getNonEmptyString(payload.message) ??
    getNonEmptyString(payload.error)
  );
};

export const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!isRecord(error)) return undefined;

  const responseData = isRecord(error.response)
    ? error.response.data
    : undefined;

  return (
    getPayloadMessage(responseData) ??
    getPayloadMessage(error.data) ??
    getJsonApiErrorMessage(error) ??
    getCodedErrorMessage(error)
  );
};

/**
 * atlas query endpoints that don't convert their error (e.g. the analytics
 * endpoints in `redux/api/atlas/enhancements.ts`) surface a raw
 * `JsonApiError` — `{ errors: [...] }` at the top level, not nested under
 * `.response.data`/`.data`. Narrowly scoped to that exact shape so it doesn't
 * pick up unrelated `.message`/`.error` fields off plain `Error`/`FETCH_ERROR`
 * objects the way the general-purpose `getPayloadMessage` does for response
 * payloads.
 */
const getJsonApiErrorMessage = (error: unknown): string | undefined => {
  if (!isRecord(error) || !Array.isArray(error.errors)) return undefined;

  const firstError = error.errors[0];
  return isRecord(firstError)
    ? (getNonEmptyString(firstError.detail) ??
        getNonEmptyString(firstError.title))
    : undefined;
};

/**
 * Services that install their own `errorHandler` (atlas throws `JsonApiError`)
 * never expose a `response`/`data` payload, so their methods convert 4xx
 * failures to a `CodedError` and carry the server's detail on `message`. The
 * `statusCode` check keeps this narrow — a plain `new Error(msg, { cause })`
 * isn't treated as an API error.
 */
const getCodedErrorMessage = (error: unknown): string | undefined => {
  if (!isCodedError(error)) return undefined;
  if (typeof error.cause?.statusCode !== 'number') return undefined;

  return getNonEmptyString(error.message);
};
